import {createBrowserRouter, Outlet, redirect} from 'react-router-dom';

import {adminPortalChildren} from './router.adminPortal';
import {learnerPortalChildren} from './router.learnerPortal';
import {managerPortalChildren} from './router.managerPortal';
import {AuthRedirect} from '../modules/auth/pages/AuthRedirect';
import {AppShell} from '../modules/common/components/appShell/AppShell';
import {ErrorBoundary} from '../modules/common/components/ErrorBoundary';
import {
  homeLinks,
  managerPortalLinks,
  learnerPortalLinks,
  adminPortalLinks,
} from '../modules/common/constants/navigation';
import {routes} from '../modules/common/constants/routes';
import {useAuthStore} from '../modules/common/stores/authStore';
import {useBreadcrumbStore} from '../modules/common/stores/breadcrumbStore';
import {useNavigationStore} from '../modules/common/stores/navigationStore';
import {useThemeStore} from '../modules/common/stores/themeStore';
import {Home} from '../modules/home/pages/Home';
import Profile from '../modules/profile/pages/Profile';
import {OrganisationWrapper} from '../OrganisationWrapper';
import {NestedThemeProvider} from '../theme/NestedThemeProvider';
import {ThemeLearnerPortalPalette} from '../theme/ThemeLearnerPortalPalette';

const resetRouteLoaders = (): null => {
  useBreadcrumbStore.setState({breadcrumbs: []});
  useNavigationStore.setState({links: homeLinks});
  useThemeStore.setState({palette: null});
  return null;
};

export const router = createBrowserRouter([
  {
    path: routes.landing,
    element: (
      <NestedThemeProvider>
        <AppShell>
          <OrganisationWrapper>
            <Outlet />
          </OrganisationWrapper>
        </AppShell>
      </NestedThemeProvider>
    ),
    ErrorBoundary,
    loader: (): null | Response => {
      const {expiresAt} = useAuthStore.getState();
      const {token} = useAuthStore.getState();
      if (token == null || (expiresAt != null && expiresAt <= new Date())) {
        return redirect('/login');
      }
      return null;
    },
    children: [
      {
        path: '',
        element: <Home />,
        loader: resetRouteLoaders,
      },
      {
        path: routes.managerPortal.get(),
        element: <Outlet />,
        children: managerPortalChildren,
        loader: (): null => {
          useThemeStore.setState({palette: ThemeLearnerPortalPalette});
          useNavigationStore.setState({links: managerPortalLinks});
          return null;
        },
      },
      {
        path: routes.learnerPortal.get(),
        element: <Outlet />,
        children: learnerPortalChildren,
        loader: (): null => {
          useThemeStore.setState({palette: ThemeLearnerPortalPalette});
          useNavigationStore.setState({links: learnerPortalLinks});
          return null;
        },
      },
      {
        path: routes.adminPortal.get(),
        element: <Outlet />,
        children: adminPortalChildren,
        loader: (): null => {
          useThemeStore.setState({palette: null});
          useNavigationStore.setState({links: adminPortalLinks});
          return null;
        },
      },
      {
        path: '/profile',
        element: <Profile />,
        loader: (): null => {
          useBreadcrumbStore.setState({breadcrumbs: [{label: 'Profile'}]});
          useNavigationStore.setState({links: homeLinks});
          useThemeStore.setState({palette: null});
          return null;
        },
      },
      {
        path: routes.unauthorised,
        lazy: async (): Promise<{Component: () => JSX.Element}> =>
          await import('./Unauthorised'),
        loader: resetRouteLoaders,
      },
    ],
  },
  {
    path: routes.login,
    element: <AuthRedirect />,
    ErrorBoundary,
    loader: resetRouteLoaders,
  },
  {
    path: routes.authRedirect,
    element: <AuthRedirect />,
    ErrorBoundary,
    loader: resetRouteLoaders,
  },
  {
    path: routes.notFound,
    lazy: async (): Promise<{Component: () => JSX.Element}> =>
      await import('./NotFound'),
    ErrorBoundary,
    loader: resetRouteLoaders,
  },
]);
